import { CustomerCompanyMessage } from '../../types/CustomerCompanyMessage';
import { CustomerSettingsMessage } from '../../types/CustomerSettingsMessage';
import { Switch } from '../cdl/Switch/Switch';
import { Text } from '../cdl/Text/Text';
import { SupplierGroupMultiSelect } from '../common/form-elements/SupplierGroupSelect/SupplierGroupMultiSelect';
import { translate } from '../common/helpers/translate.helper';
import { Box } from '../common/ui/Box';

import { MailSettings } from './components/MailSettings';
import { useCustomerAdminSettingsOptimisticMutation } from './hooks/useCustomerAdminSettingsOptimisticMutation';
import { useCustomerSettingsOptimisticMutation } from './hooks/useCustomerSettingsOptimisticMutation';

interface CustomerAdminSettingsProps {
    companyDetails: CustomerCompanyMessage;
    customerSettings: CustomerSettingsMessage;
}

export const CustomerAdminSettings = ({ companyDetails, customerSettings }: CustomerAdminSettingsProps) => {
    const companySettingsMutation = useCustomerAdminSettingsOptimisticMutation(companyDetails.id);
    const customerSettingsMutation = useCustomerSettingsOptimisticMutation(companyDetails.id);

    const handleSettingChange = (newCompanyData: CustomerCompanyMessage) => {
        companySettingsMutation.mutate({
            id: companyDetails.id,
            customerData: newCompanyData,
        });
    };

    const handleSpotExcludedSupplierGroupIdsChange = (supplierGroupIds: readonly string[]) => {
        customerSettingsMutation.mutate({
            id: companyDetails.id,
            settings: {
                ...customerSettings,
                spotExcludedSupplierGroupIds: [...supplierGroupIds],
            },
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={24}>
            <Text variant="subtitle1" as="h2">
                {translate('company.adminSettings')}
            </Text>

            <Box display="flex" flexDirection="column" gap={16}>
                <Switch
                    label={translate('company.testCompany')}
                    checked={companyDetails.testCompany}
                    onChange={(checked) => handleSettingChange({ ...companyDetails, testCompany: checked })}
                />

                <Switch
                    label={translate('company.trial')}
                    checked={companyDetails.trial}
                    onChange={(checked) => handleSettingChange({ ...companyDetails, trial: checked })}
                />

                <Box display="flex" flexDirection="column" gap={8}>
                    <Switch
                        label={translate('company.spotallowed')}
                        checked={companyDetails.spotAllowed}
                        onChange={(checked) => handleSettingChange({ ...companyDetails, spotAllowed: checked })}
                    />

                    {companyDetails.spotAllowed && (
                        <SupplierGroupMultiSelect
                            value={customerSettings.spotExcludedSupplierGroupIds ?? []}
                            onChange={handleSpotExcludedSupplierGroupIdsChange}
                            label={translate('company.spotSupplierExclusions')}
                            placeholder={translate('supplierGroupSelect.placeholder')}
                            spotAllowed
                        />
                    )}
                </Box>

                <MailSettings
                    mailSettings={companyDetails.mailSettings}
                    onSettingChange={(newMailSettings) =>
                        handleSettingChange({ ...companyDetails, mailSettings: newMailSettings })
                    }
                />
            </Box>
        </Box>
    );
};
